import React from "react";
import { IMAGES } from "@/lib/site";
import { Fish, ShieldAlert } from "lucide-react";
import { LuxeOverline } from "./LuxeOverline";
import { useContent } from "@/lib/content";

const rules = [
  "Barbless hooks only — no micro-barbs",
  "No boilies larger than 15mm",
  "No nut baits or bloodworm",
  "Unhooking mats & landing nets required",
  "Residents only — no day fishing, no matches",
  "Lodge guests: 1 complimentary rod per person per stay — extra rods £6 each per day",
];

const Fishing = () => {
  const c = useContent();
  const fishingImage = c("fishing.image") || IMAGES.lake;
  return (
    <section
      id="fishing"
      data-testid="fishing-section"
      className="relative py-24 md:py-36 bg-[#F5EFE6] overflow-hidden"
    >
      {/* Ambient rose-gold glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(ellipse at 15% 80%, rgba(183,110,121,0.15) 0%, transparent 55%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          <div className="lg:col-span-6 order-2 lg:order-1">
            {/* Framed image with rose-gold inset */}
            <div className="relative">
              <div className="absolute -inset-3 border border-[#B76E79]/30 rounded-3xl pointer-events-none" />
              <div className="absolute -inset-1 border border-[#C9A47E]/20 rounded-2xl pointer-events-none" />
              <div className="img-frame aspect-[4/5] relative">
                <img src={fishingImage} alt="Fishing lake at dawn" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 lg:pt-12">
            <LuxeOverline>{c("fishing.overline", "The Fishing")}</LuxeOverline>
            <h2 className="font-serif text-4xl md:text-5xl mt-8 text-[#14100F] leading-[1.05]">
              {c("fishing.title_pre", "Walnut and Daisy Lakes")}{" "}
              <em className="italic rose-gold-text">
                {c("fishing.title_em", "Plenty of carp fishing")}
              </em>
            </h2>
            <p className="mt-6 text-[#3A322E] text-base md:text-lg leading-relaxed">
              {c(
                "fishing.body",
                "Our two private waters are heavily stocked with mirror and common carp, along with bream and rudd. Eight to nine-pounders are regulars; heavier fish make an appearance for the patient rod. Fishing is for our staying residents only — we don't offer day fishing or host matches, so the lakes stay quiet and the fish rested from opening day to the end of the season. Lodge guests fish free with one complimentary rod per person per stay; additional rods are £6 each per day. Camping guests fish for £6 a day with one rod, or £10 a day with two."
              )}
            </p>

            {/* Luxe stat cards */}
            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="relative bg-white/60 backdrop-blur-sm border border-[#DDD1BE] rounded-xl p-5 overflow-hidden">
                <span className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#B76E79] to-[#C9A47E]" />
                <div className="font-serif text-4xl rose-gold-text leading-none">
                  {c("fishing.stat1_v", "Free")}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[#6A5F58]">
                  {c("fishing.stat1_l", "For lodge guests")}
                </div>
              </div>
              <div className="relative bg-white/60 backdrop-blur-sm border border-[#DDD1BE] rounded-xl p-5 overflow-hidden">
                <span className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#C9A47E] to-[#B76E79]" />
                <div className="font-serif text-4xl text-[#0F0E0E] leading-none">
                  {c("fishing.stat2_v", "£6/day")}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[#6A5F58]">
                  {c("fishing.day_ticket", "Camping guests · 1 rod · 2 rods £10/day")}
                </div>
              </div>
            </div>

            <div
              className="mt-12 relative p-8 md:p-10 bg-white border border-[#DDD1BE] rounded-2xl shadow-sm overflow-hidden"
              data-testid="fishing-rules"
            >
              <span className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E]" />
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F5EFE6] to-[#E4B2A8] flex items-center justify-center border border-[#B76E79]/30">
                  <Fish size={16} className="text-[#0F0E0E]" strokeWidth={1.4} />
                </span>
                <p className="overline text-[#0F0E0E]">Rules of the water</p>
              </div>
              <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                {rules.map((r) => (
                  <li
                    key={r}
                    className="flex items-start gap-3 text-sm text-[#3A322E] leading-relaxed"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rotate-45 bg-[#B76E79] flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>

              {/* Highlighted rod-licence requirement — impossible to miss */}
              <div
                data-testid="fishing-licence-notice"
                className="mt-6 rounded-xl border-l-4 border-[#B76E79] bg-gradient-to-r from-[#B76E79]/12 via-[#E4B2A8]/8 to-transparent p-5 md:p-6"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 w-8 h-8 rounded-full bg-[#B76E79] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <ShieldAlert size={16} className="text-[#F5EFE6]" strokeWidth={2} />
                  </span>
                  <div>
                    <p className="font-serif text-lg md:text-xl text-[#0F0E0E] leading-tight">
                      A valid Environment Agency rod licence is required
                    </p>
                    <p className="mt-2 text-sm text-[#3A322E] leading-relaxed">
                      Every angler aged 13 or over <strong>must</strong> hold a valid Environment Agency rod
                      fishing licence and be able to <strong>produce it on request</strong> before casting a
                      line. Anyone unable to show a licence will not be permitted to fish — no exceptions.
                    </p>
                    <a
                      href="https://www.gov.uk/fishing-licences/buy-a-fishing-licence"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fishing-licence-link"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#B76E79] hover:text-[#0F0E0E] transition-colors font-medium"
                    >
                      Buy a licence at gov.uk →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Fishing;
